import { useState } from 'react';
import {
  History,
  Clock,
  User,
  ChevronRight,
  RotateCcw,
  Eye,
  X,
  GitCommit,
  GitBranch,
  Layers,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatDateTime, cn } from '@/lib/utils';
import type { WorkflowNode, WorkflowEdge } from '@/types';

export interface WorkflowVersion {
  id: string;
  version: number;
  title: string;
  summary: string;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  author: {
    id: string;
    name: string;
    avatarUrl?: string;
  };
  createdAt: string;
  changes: {
    nodesAdded: number;
    nodesRemoved: number;
    edgesAdded: number;
    edgesRemoved: number;
  };
}

interface WorkflowVersionHistoryProps {
  versions: WorkflowVersion[];
  currentVersion: number;
  onRestore: (version: WorkflowVersion) => void;
  isLoading?: boolean;
}

export function WorkflowVersionHistory({
  versions,
  currentVersion,
  onRestore,
  isLoading,
}: WorkflowVersionHistoryProps) {
  const [selectedVersion, setSelectedVersion] = useState<WorkflowVersion | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const handlePreview = (version: WorkflowVersion) => {
    setSelectedVersion(version);
    setPreviewOpen(true);
  };

  const handleRestore = (version: WorkflowVersion) => {
    onRestore(version);
    setPreviewOpen(false);
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm">
          <History className="mr-1 h-4 w-4" />
          History
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[400px] sm:w-[540px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Workflow Version History
          </SheetTitle>
        </SheetHeader>

        <div className="mt-6">
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="h-24 animate-pulse rounded-lg bg-muted"
                />
              ))}
            </div>
          ) : versions.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <GitCommit className="mx-auto h-12 w-12 opacity-50" />
              <p className="mt-4">No version history yet</p>
              <p className="mt-1 text-sm">
                Version history will appear here when you save workflow changes.
              </p>
            </div>
          ) : (
            <ScrollArea className="h-[calc(100vh-150px)]">
              <div className="space-y-2 pr-4">
                {versions.map((version) => (
                  <WorkflowVersionItem
                    key={version.id}
                    version={version}
                    isCurrent={version.version === currentVersion}
                    onPreview={() => handlePreview(version)}
                    onRestore={() => handleRestore(version)}
                  />
                ))}
              </div>
            </ScrollArea>
          )}
        </div>
      </SheetContent>

      {/* Version Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Version {selectedVersion?.version} Preview</span>
              <div className="flex items-center gap-2">
                {selectedVersion &&
                  selectedVersion.version !== currentVersion && (
                    <Button
                      size="sm"
                      onClick={() =>
                        selectedVersion && handleRestore(selectedVersion)
                      }
                    >
                      <RotateCcw className="mr-1 h-4 w-4" />
                      Restore this version
                    </Button>
                  )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setPreviewOpen(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>

          {selectedVersion && (
            <div className="mt-4">
              <div className="mb-4 flex items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <User className="h-4 w-4" />
                  {selectedVersion.author.name}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {formatDateTime(selectedVersion.createdAt)}
                </span>
              </div>

              {/* Workflow Stats */}
              <div className="mb-4 grid grid-cols-2 gap-4">
                <div className="rounded-lg border p-3">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Layers className="h-4 w-4" />
                    Nodes
                  </div>
                  <p className="mt-1 text-2xl font-bold">
                    {selectedVersion.nodes.length}
                  </p>
                  <div className="mt-1 flex gap-2 text-xs">
                    <span className="text-green-600">
                      +{selectedVersion.changes.nodesAdded}
                    </span>
                    <span className="text-red-600">
                      -{selectedVersion.changes.nodesRemoved}
                    </span>
                  </div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <GitBranch className="h-4 w-4" />
                    Edges
                  </div>
                  <p className="mt-1 text-2xl font-bold">
                    {selectedVersion.edges.length}
                  </p>
                  <div className="mt-1 flex gap-2 text-xs">
                    <span className="text-green-600">
                      +{selectedVersion.changes.edgesAdded}
                    </span>
                    <span className="text-red-600">
                      -{selectedVersion.changes.edgesRemoved}
                    </span>
                  </div>
                </div>
              </div>

              {/* Node List */}
              <ScrollArea className="h-[40vh] rounded-lg border bg-muted/30 p-4">
                <h4 className="mb-3 font-medium">Workflow Structure</h4>
                <div className="space-y-2">
                  {selectedVersion.nodes.map((node) => (
                    <div
                      key={node.id}
                      className="flex items-center justify-between rounded border bg-background p-2 text-sm"
                    >
                      <div className="flex items-center gap-2">
                        <Badge
                          variant="outline"
                          className={cn(
                            'text-xs',
                            node.data.type === 'start' && 'border-green-300 bg-green-50',
                            node.data.type === 'end' && 'border-red-300 bg-red-50',
                            node.data.type === 'decision' && 'border-yellow-300 bg-yellow-50',
                            node.data.type === 'task' && 'border-gray-300 bg-gray-50',
                            node.data.type === 'process' && 'border-blue-300 bg-blue-50'
                          )}
                        >
                          {node.data.type || 'task'}
                        </Badge>
                        <span>{node.data.label}</span>
                      </div>
                      {node.data.status && (
                        <Badge
                          variant="secondary"
                          className={cn(
                            'text-xs',
                            node.data.status === 'completed' && 'bg-green-100 text-green-700',
                            node.data.status === 'running' && 'bg-blue-100 text-blue-700',
                            node.data.status === 'failed' && 'bg-red-100 text-red-700',
                            node.data.status === 'pending' && 'bg-yellow-100 text-yellow-700'
                          )}
                        >
                          {node.data.status}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>

                {selectedVersion.edges.length > 0 && (
                  <>
                    <h4 className="mb-3 mt-6 font-medium">Connections</h4>
                    <div className="space-y-1 text-sm text-muted-foreground">
                      {selectedVersion.edges.map((edge) => (
                        <div key={edge.id} className="flex items-center gap-2">
                          <span className="font-mono">{edge.source}</span>
                          <ChevronRight className="h-4 w-4" />
                          <span className="font-mono">{edge.target}</span>
                          {edge.label && (
                            <Badge variant="outline" className="ml-2 text-xs">
                              {edge.label}
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </ScrollArea>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Sheet>
  );
}

function WorkflowVersionItem({
  version,
  isCurrent,
  onPreview,
  onRestore,
}: {
  version: WorkflowVersion;
  isCurrent: boolean;
  onPreview: () => void;
  onRestore: () => void;
}) {
  const totalChanges =
    version.changes.nodesAdded +
    version.changes.nodesRemoved +
    version.changes.edgesAdded +
    version.changes.edgesRemoved;

  return (
    <div
      className={cn(
        'group relative rounded-lg border p-3 transition-colors hover:bg-muted/50',
        isCurrent && 'border-primary bg-primary/5'
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Badge
              variant={isCurrent ? 'default' : 'outline'}
              className="text-xs"
            >
              v{version.version}
            </Badge>
            {isCurrent && (
              <Badge variant="secondary" className="text-xs">
                Current
              </Badge>
            )}
          </div>

          <h4 className="mt-1.5 font-medium truncate">{version.title}</h4>

          <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
            {version.summary}
          </p>

          <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <User className="h-3 w-3" />
              {version.author.name}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatDateTime(version.createdAt)}
            </span>
          </div>

          <div className="mt-2 flex items-center gap-2 text-xs">
            <span className="flex items-center gap-1">
              <Layers className="h-3 w-3" />
              {version.nodes.length} nodes
            </span>
            <span className="flex items-center gap-1">
              <GitBranch className="h-3 w-3" />
              {version.edges.length} edges
            </span>
            {totalChanges > 0 && (
              <Badge variant="outline" className="text-xs">
                {totalChanges} changes
              </Badge>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button variant="ghost" size="icon" onClick={onPreview}>
            <Eye className="h-4 w-4" />
          </Button>
          {!isCurrent && (
            <Button variant="ghost" size="icon" onClick={onRestore}>
              <RotateCcw className="h-4 w-4" />
            </Button>
          )}
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>
    </div>
  );
}

// Mock workflow versions for demo
export function useMockWorkflowVersionHistory(): WorkflowVersion[] {
  return [
    {
      id: '1',
      version: 3,
      title: 'Added approval decision node',
      summary:
        'Added decision node for approval workflow with Yes/No branches and notification tasks.',
      nodes: [
        {
          id: 'start',
          type: 'custom',
          position: { x: 250, y: 50 },
          data: { label: 'Start', type: 'start', status: 'completed' },
        },
        {
          id: 'task-1',
          type: 'custom',
          position: { x: 250, y: 150 },
          data: { label: 'Submit Request', type: 'task', status: 'completed' },
        },
        {
          id: 'decision-1',
          type: 'custom',
          position: { x: 250, y: 250 },
          data: { label: 'Approval?', type: 'decision', status: 'running' },
        },
        {
          id: 'task-2',
          type: 'custom',
          position: { x: 100, y: 350 },
          data: { label: 'Send Approval', type: 'task', status: 'pending' },
        },
        {
          id: 'task-3',
          type: 'custom',
          position: { x: 400, y: 350 },
          data: { label: 'Send Rejection', type: 'task', status: 'pending' },
        },
        {
          id: 'end',
          type: 'custom',
          position: { x: 250, y: 450 },
          data: { label: 'End', type: 'end', status: 'pending' },
        },
      ],
      edges: [
        { id: 'e1', source: 'start', target: 'task-1' },
        { id: 'e2', source: 'task-1', target: 'decision-1' },
        { id: 'e3', source: 'decision-1', target: 'task-2', label: 'Yes' },
        { id: 'e4', source: 'decision-1', target: 'task-3', label: 'No' },
        { id: 'e5', source: 'task-2', target: 'end' },
        { id: 'e6', source: 'task-3', target: 'end' },
      ],
      author: { id: '1', name: 'John Doe' },
      createdAt: new Date().toISOString(),
      changes: { nodesAdded: 2, nodesRemoved: 0, edgesAdded: 3, edgesRemoved: 1 },
    },
    {
      id: '2',
      version: 2,
      title: 'Added processing task',
      summary: 'Added task node for request processing step between start and end.',
      nodes: [
        {
          id: 'start',
          type: 'custom',
          position: { x: 250, y: 50 },
          data: { label: 'Start', type: 'start', status: 'completed' },
        },
        {
          id: 'task-1',
          type: 'custom',
          position: { x: 250, y: 150 },
          data: { label: 'Submit Request', type: 'task', status: 'completed' },
        },
        {
          id: 'end',
          type: 'custom',
          position: { x: 250, y: 250 },
          data: { label: 'End', type: 'end', status: 'pending' },
        },
      ],
      edges: [
        { id: 'e1', source: 'start', target: 'task-1' },
        { id: 'e2', source: 'task-1', target: 'end' },
      ],
      author: { id: '2', name: 'Jane Smith' },
      createdAt: new Date(Date.now() - 86400000).toISOString(),
      changes: { nodesAdded: 1, nodesRemoved: 0, edgesAdded: 1, edgesRemoved: 0 },
    },
    {
      id: '3',
      version: 1,
      title: 'Initial workflow',
      summary: 'Created initial workflow with start and end nodes.',
      nodes: [
        {
          id: 'start',
          type: 'custom',
          position: { x: 250, y: 50 },
          data: { label: 'Start', type: 'start', status: 'completed' },
        },
        {
          id: 'end',
          type: 'custom',
          position: { x: 250, y: 150 },
          data: { label: 'End', type: 'end', status: 'pending' },
        },
      ],
      edges: [{ id: 'e1', source: 'start', target: 'end' }],
      author: { id: '1', name: 'John Doe' },
      createdAt: new Date(Date.now() - 172800000).toISOString(),
      changes: { nodesAdded: 2, nodesRemoved: 0, edgesAdded: 1, edgesRemoved: 0 },
    },
  ];
}
